import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, Loader2, User, Mail, Phone, BookOpen, Building2, Calendar,
  Globe, MousePointer, FileText, Tag, ShieldCheck, Send, Plus, AlertTriangle,
  ArrowRightCircle, CreditCard, Users as UsersIcon, CheckCircle2, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { leadSourceLabel } from "../../lib/leadSources";
import { canResendWelcome, welcomeTitle } from "../../lib/leadWelcome";
import AssignSelector from "./AssignSelector";
import LeadEnrollmentsCard from "./LeadEnrollmentsCard";
import LeadCallPanel from "./LeadCallPanel";
import DatePickerField from "./DatePickerField";
import { OUTCOME_BY_ID, nextCallLabel, todayRo } from "../../lib/leadCalls";

const STATUSES = [
  { v: "lead_nou", label: "Nou" },
  { v: "contactat", label: "Contactat" },
  { v: "interesat", label: "Interesat" },
  { v: "convertit", label: "Convertit" },
  { v: "pierdut", label: "Pierdut" },
  { v: "duplicat", label: "Duplicat" },
];

const EVENT_LABELS = {
  lead_created: "Lead creat",
  status_changed: "Status schimbat",
  note_added: "Notiță adăugată",
  email_resent: "Email retrimis",
  lead_converted: "Lead convertit în înscriere",
  purchase_pending: "Plată inițiată",
  purchase_paid: "Plată finalizată",
  purchase_failed: "Plată eșuată",
  purchase_abandoned: "Plată abandonată",
  purchase_cancelled: "Plată anulată",
  purchase_pending_registration: "Înscriere fără plată",
  purchase_duplicate: "Plată duplicat",
  call_logged: "Apel înregistrat",
  lead_auto_converted: "Convertit automat (s-a înscris pe site)",
  enrollment_payment_expired: "Termenul de plată a expirat",
  enrollment_payment_stopped: "Plata nu a trecut",
  assigned_to_changed: "Responsabil schimbat",
  lead_assigned: "Lead atribuit",
  lead_unassigned: "Atribuire scoasă",
};

export default function LeadDrawer({ leadId, canMutate, currentRole, onClose, onUpdated, onConverted, onOpenEnrollment }) {
  const [lead, setLead] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [tab, setTab] = useState("details");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const drawerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [d, tl, dup] = await Promise.all([
        api.get(`/admin/leads/${leadId}`),
        api.get(`/admin/leads/${leadId}/timeline`),
        api.get(`/admin/leads/${leadId}/duplicates`),
      ]);
      setLead(d.data);
      setTimeline(tl.data.events || []);
      setDuplicates(dup.data.items || []);
    } catch {
      toast.error("Nu am putut încărca leadul");
      onClose();
    }
  }, [leadId, onClose]);

  useEffect(() => { load(); }, [load]);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setStatus = async (status) => {
    try {
      const r = await api.patch(`/admin/leads/${leadId}`, { status });
      setLead(r.data);
      onUpdated?.(r.data);
      toast.success("Status actualizat");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const setFollowUp = async (date) => {
    try {
      // "" clears it: the route ignores a null follow_up_date.
      const r = await api.patch(`/admin/leads/${leadId}`, { follow_up_date: date || "" });
      setLead(r.data);
      onUpdated?.(r.data);
      toast.success("Follow-up actualizat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/leads/${leadId}/notes`, { text: noteText });
      setNoteText("");
      toast.success("Notiță adăugată");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    } finally {
      setSavingNote(false);
    }
  };

  const resendEmail = async () => {
    try {
      await api.post(`/admin/leads/${leadId}/resend-welcome`);
      toast.success("Am retrimis emailul.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const [showConvert, setShowConvert] = useState(false);
  const isConverted = lead?.status === "convertit" || !!lead?.enrollment_id;
  const canConvert = canMutate && !isConverted && !lead?.deleted_at;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="lead-drawer">
      <div
        ref={drawerRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F5F6F6] w-full max-w-2xl h-full overflow-y-auto shadow-2xl border-l border-[#E5E7EB]"
        role="dialog" aria-modal="true"
      >
        {!lead ? (
          <div className="p-10 flex items-center gap-2 text-[#6B7280]">
            <Loader2 size={16} className="animate-spin" /> Se încarcă…
          </div>
        ) : (
          <>
            <header className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB] p-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="font-display text-2xl font-bold text-[#373A45] truncate">{lead.prenume} {lead.nume}</h2>
                <p className="text-sm text-[#6B7280] truncate">{lead.email} · {lead.telefon}</p>
              </div>
              <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45] shrink-0" aria-label="Închide" data-testid="drawer-close">
                <X size={20} />
              </button>
            </header>

            {/* Row 1 — Status & ownership */}
            <div className="px-5 pt-4 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="drawer-status-row">
              <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#6B7280] font-semibold">
                Status
                <select
                  value={lead.status || "nou"}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={!canMutate}
                  className="rounded-full px-3 py-1.5 text-xs font-semibold border border-[#E5E7EB] bg-white text-[#373A45] disabled:opacity-50"
                  data-testid="drawer-status"
                >
                  {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </label>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#6B7280] font-semibold">
                <span>Atribuit lui</span>
                <AssignSelector
                  endpoint={`leads/${leadId}/assign`}
                  docId={leadId}
                  currentAssignedToUserId={lead.assigned_to_user_id}
                  currentAssignedToEmail={lead.assigned_to_email}
                  canManage={currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin"}
                  onChanged={(res) => {
                    setLead((prev) => prev ? { ...prev, ...res } : prev);
                    onUpdated?.({ id: leadId, ...res });
                  }}
                  testIdPrefix="lead-assign"
                  showLabel={false}
                />
              </div>
              {isConverted && (
                <span
                  className="ml-auto rounded-full px-3 py-1.5 text-xs font-bold bg-[#519872]/15 text-[#1f6045] inline-flex items-center gap-1.5"
                  data-testid="drawer-converted-badge"
                >
                  <CheckCircle2 size={12} /> Convertit
                </span>
              )}
            </div>

            {/* Row 2 — Actions */}
            {(canMutate || canConvert) && (
              <div className="px-5 pt-3 flex flex-wrap items-center gap-2" data-testid="drawer-actions-row">
                {canMutate && canResendWelcome(lead) && (
                  <button
                    onClick={resendEmail}
                    title={welcomeTitle(lead)}
                    className="rounded-full px-3 py-1.5 text-xs font-semibold border border-[#E5E7EB] bg-white hover:bg-[#F5F6F6] text-[#373A45] inline-flex items-center gap-1.5"
                    data-testid="drawer-resend"
                  >
                    <Send size={12} /> Retrimite email
                  </button>
                )}
                {canConvert && (
                  <button
                    onClick={() => setShowConvert(true)}
                    className="ml-auto rounded-full px-3 py-1.5 text-xs font-bold bg-[#965EA7] hover:bg-[#7A4986] text-white inline-flex items-center gap-1.5 shadow-[0_2px_8px_0_rgba(150,94,167,0.35)]"
                    data-testid="drawer-convert-btn"
                  >
                    <ArrowRightCircle size={14} /> Convertește în înscriere
                  </button>
                )}
              </div>
            )}

            <nav className="px-5 mt-5 border-b border-[#E5E7EB] flex gap-1">
              <TabBtn active={tab === "details"} onClick={() => setTab("details")} testid="drawer-tab-details">Detalii</TabBtn>
              <TabBtn active={tab === "timeline"} onClick={() => setTab("timeline")} testid="drawer-tab-timeline">Timeline</TabBtn>
              <TabBtn active={tab === "notes"} onClick={() => setTab("notes")} testid="drawer-tab-notes">Notițe ({lead.notes_count || 0})</TabBtn>
            </nav>

            <div className="p-5 space-y-5">
              {tab === "details" && (
                <>
                  <LeadEnrollmentsCard leadId={leadId} canMutate={canMutate} onOpenEnrollment={onOpenEnrollment} />
                  <CallsSection
                    lead={lead}
                    canMutate={canMutate}
                    onFollowUp={setFollowUp}
                    onLogged={(updated) => {
                      setLead((prev) => ({ ...prev, ...updated }));
                      onUpdated?.(updated);
                    }}
                  />
                  <DetailsView lead={lead} duplicates={duplicates} />
                </>
              )}
              {tab === "timeline" && <TimelineView events={timeline} />}
              {tab === "notes" && (
                <NotesView
                  notes={lead.notes || []}
                  canMutate={canMutate}
                  noteText={noteText}
                  setNoteText={setNoteText}
                  saving={savingNote}
                  onSubmit={submitNote}
                />
              )}
            </div>
          </>
        )}
      </div>

      {showConvert && lead && (
        <ConvertLeadModal
          lead={lead}
          onClose={() => setShowConvert(false)}
          onConverted={(result) => {
            setShowConvert(false);
            toast.success(
              result.mode === "online_payment"
                ? "Lead convertit · link de plată trimis pe email."
                : "Lead convertit · email de confirmare înscriere trimis."
            );
            // Bubble up so the parent can switch to Enrollments tab + open the new drawer.
            onConverted?.(result.enrollment_id);
            // Refresh the lead doc so the badge updates / button hides.
            load();
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Convert Lead → Enrollment modal (Var B native flow)
// Pre-fills name/email/phone/etc from lead; admin only picks group + mode.
// ============================================================
function ConvertLeadModal({ lead, onClose, onConverted }) {
  const [groups, setGroups] = useState(null);
  const [groupId, setGroupId] = useState("");
  const [mode, setMode] = useState("registration_only");
  // A negotiated price, set here rather than in a second step, so the person
  // gets one e-mail with the right amount (23.09.2026).
  const [amount, setAmount] = useState("");
  const [amountReason, setAmountReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/admin/groups")
      .then((r) => {
        const all = (r.data || []).filter((g) => !g.archived);
        setGroups(all);
        // Auto-select the first active group with available capacity
        const firstActive = all.find(
          (g) => g.status === "active" && (g.max_seats === 0 || (g.enrolled_count || 0) < g.max_seats)
        );
        if (firstActive) setGroupId(firstActive.id);
      })
      .catch(() => {
        toast.error("Nu am putut încărca grupele.");
        setGroups([]);
      });
  }, []);

  const submit = async () => {
    // M851 — group is REQUIRED for online_payment, OPTIONAL for registration_only.
    if (mode === "online_payment" && !groupId) {
      toast.error("Trebuie să selectezi o grupă pentru a trimite link de plată.");
      return;
    }
    if (mode === "online_payment" && amount.trim() && !(Number(amount) > 0)) {
      toast.error("Suma de plată trebuie să fie un număr mai mare ca zero.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/leads/${lead.id}/convert`, {
        group_id: groupId || null,
        mode,
        ...(mode === "online_payment" && amount.trim()
          ? { amount: Number(amount), amount_reason: amountReason.trim() || undefined }
          : {}),
      });
      onConverted(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversia a eșuat.");
    } finally {
      setSubmitting(false);
    }
  };

  // M851 — derived UI state. We compute these once so the JSX below stays
  // focused on rendering rather than re-running the same conditions.
  const isOnlinePayment = mode === "online_payment";
  const noGroupsAvailable = Array.isArray(groups) && groups.length === 0;
  const groupRequired = isOnlinePayment;
  const submitDisabled =
    submitting ||
    (groupRequired && !groupId) ||
    (groupRequired && noGroupsAvailable);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="convert-lead-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-bold text-[#373A45]">Convertește lead în înscriere</h3>
            <p className="text-xs text-[#6B7280] mt-1">
              Datele leadului sunt deja pre-completate. Alege grupa și modul de înscriere.
            </p>
          </div>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
            <X size={20} />
          </button>
        </header>

        <div className="p-6 space-y-5">
          {/* Lead summary (read-only, pre-filled) */}
          <div className="rounded-2xl border border-[#E5E7EB] bg-[#F5F6F6]/70 p-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-3">
              Date pre-completate din lead
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <SummaryRow label="Nume" value={`${lead.prenume || ""} ${lead.nume || ""}`.trim()} />
              <SummaryRow label="Email" value={lead.email} />
              <SummaryRow label="Telefon" value={lead.telefon} />
              <SummaryRow label="Specializare" value={lead.specializare || "—"} />
              <SummaryRow
                label="Unitate"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    {lead.unitate || "—"}
                    {lead.manual_school_entry && (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#FFF7E6] text-[#7A5A1B] border border-[#F5C76E]/40">manual</span>
                    )}
                  </span>
                }
              />
              <SummaryRow label="Localitate (UAT)" value={lead.uat_name || lead.city || "—"} />
              <SummaryRow label="Județ" value={lead.county || "—"} />
              <SummaryRow label="Sursă" value={leadSourceLabel(lead.source)} />
            </div>
          </div>

          {/* Group picker (M851 — optional for registration_only, required for online_payment). */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-[#373A45] flex items-center gap-2">
              <UsersIcon size={13} />
              {groupRequired
                ? "Grupă (obligatoriu pentru plată online)"
                : "Grupă asociată (opțional)"}
            </label>
            {groups === null ? (
              <div className="mt-2 text-sm text-[#6B7280] inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Se încarcă grupele…
              </div>
            ) : groups.length === 0 ? (
              <div className={`mt-2 text-sm rounded-xl px-3 py-2 ${
                groupRequired
                  ? "text-[#9b1f2c] bg-[#FDECEC]"
                  : "text-[#7A5A1B] bg-[#FFF7E6] border border-[#F5C76E]/40"
              }`} data-testid="convert-no-groups">
                {groupRequired
                  ? 'Nu există grupe active. Creează una din tab-ul „Grupe curs" înainte să trimiți link de plată.'
                  : "Poți crea înscrierea fără grupă și o poți atribui ulterior."}
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {/* M851 — explicit "no group" option for registration_only. */}
                {!groupRequired && (
                  <label
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                      groupId === ""
                        ? "border-[#965EA7] bg-[#965EA7]/[0.06]"
                        : "border-[#E5E7EB] hover:border-[#965EA7]/40"
                    }`}
                    data-testid="convert-group-option-none"
                  >
                    <input
                      type="radio"
                      name="convert-group"
                      value=""
                      checked={groupId === ""}
                      onChange={() => setGroupId("")}
                      className="mt-1 accent-[#965EA7]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#373A45]">Fără grupă deocamdată</div>
                      <div className="text-xs text-[#6B7280] mt-0.5">
                        Înscrierea poate fi atribuită ulterior dintr-o grupă activă.
                      </div>
                    </div>
                  </label>
                )}
                {groups.map((g) => {
                  const seats = Number(g.max_seats || 0);
                  const enrolled = Number(g.enrolled_count || 0);
                  const remaining = seats > 0 ? seats - enrolled : "∞";
                  const full = seats > 0 && enrolled >= seats;
                  const inactive = g.status !== "active";
                  const disabled = full || inactive;
                  const selected = groupId === g.id;
                  return (
                    <label
                      key={g.id}
                      className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                        disabled
                          ? "border-[#E5E7EB] bg-[#F5F6F6] opacity-60 cursor-not-allowed"
                          : selected
                          ? "border-[#965EA7] bg-[#965EA7]/[0.06] cursor-pointer"
                          : "border-[#E5E7EB] hover:border-[#965EA7]/40 cursor-pointer"
                      }`}
                      data-testid={`convert-group-option-${g.id}`}
                    >
                      <input
                        type="radio"
                        name="convert-group"
                        value={g.id}
                        checked={selected}
                        disabled={disabled}
                        onChange={() => setGroupId(g.id)}
                        className="mt-1 accent-[#965EA7]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#373A45]">{g.name}</div>
                        <div className="text-xs text-[#6B7280] mt-0.5">
                          Start: <strong className="text-[#373A45]">{g.start_date || "—"}</strong>
                          {g.format && <> · {g.format}</>}
                          {g.schedule_label && <> · {g.schedule_label}</>}
                        </div>
                        <div className="text-[11px] mt-1 flex items-center gap-2">
                          <span className={`rounded-full px-2 py-0.5 ${
                            inactive ? "bg-[#6B7280]/15 text-[#373A45]" :
                            full ? "bg-[#9b1f2c]/15 text-[#9b1f2c]" :
                            "bg-[#519872]/15 text-[#1f6045]"
                          }`}>
                            {inactive ? `Status: ${g.status}` : full ? "Plină" : `Locuri rămase: ${remaining}`}
                          </span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Mode picker (M851 — when no active groups exist, "online_payment"
              is disabled because a group is mandatory for the payment flow). */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">
              Tip înscriere
            </label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ModeOption
                checked={mode === "registration_only"}
                onClick={() => setMode("registration_only")}
                icon={<UsersIcon size={16} />}
                title="Doar înscriere"
                hint="Trimite email de confirmare înscriere. Grupa este opțională."
                testid="convert-mode-registration_only"
              />
              <ModeOption
                checked={mode === "online_payment"}
                onClick={() => {
                  if (noGroupsAvailable) return;
                  setMode("online_payment");
                }}
                disabled={noGroupsAvailable}
                icon={<CreditCard size={16} />}
                title="Trimite link de plată"
                hint={
                  noGroupsAvailable
                    ? "Trebuie să existe cel puțin o grupă activă."
                    : "Creează înscrierea și trimite emailul cu linkul de plată."
                }
                testid="convert-mode-online_payment"
              />
            </div>
          </div>
          {mode === "online_payment" && (
            <div className="space-y-2" data-testid="convert-amount">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">
                  Suma de plată (RON)
                </span>
                <input
                  type="number" step="0.01" min="1" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="lasă gol pentru prețul standard"
                  className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30"
                  data-testid="convert-amount-input"
                />
              </label>
              {amount.trim() && (
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">
                    De ce altă sumă (notă internă)
                  </span>
                  <input
                    type="text" value={amountReason} maxLength={200}
                    onChange={(e) => setAmountReason(e.target.value)}
                    placeholder="ex. preț negociat pentru 3 profesori din aceeași școală"
                    className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30"
                    data-testid="convert-amount-reason"
                  />
                  <span className="mt-1 block text-[11px] text-[#6B7280]">
                    Nota rămâne pe înscriere, ca să se vadă de ce suma diferă de preț. Participantul nu o vede.
                  </span>
                </label>
              )}
            </div>
          )}
          {groupRequired && !groupId && groups && groups.length > 0 && (
            <div
              className="rounded-xl bg-[#FFF7E6] border border-[#F5C76E]/40 px-3 py-2 text-xs text-[#7A5A1B]"
              data-testid="convert-group-required-hint"
            >
              Selectează o grupă pentru a putea trimite linkul de plată.
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-6 py-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-full px-4 py-2 text-sm font-semibold border border-[#E5E7EB] hover:bg-[#F5F6F6] disabled:opacity-50"
            data-testid="convert-cancel"
          >
            Anulează
          </button>
          <button
            onClick={submit}
            disabled={submitDisabled}
            className="rounded-full px-5 py-2 text-sm font-bold bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-40 text-white inline-flex items-center gap-2 shadow-[0_2px_8px_0_rgba(150,94,167,0.35)]"
            data-testid="convert-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightCircle size={14} />}
            {mode === "online_payment" ? "Convertește & trimite plată" : "Convertește în înscriere"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</div>
      <div className="text-sm text-[#373A45] break-words">{value}</div>
    </div>
  );
}

function ModeOption({ checked, onClick, icon, title, hint, testid, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={disabled}
      className={`text-left rounded-xl border p-3 transition-colors ${
        disabled
          ? "border-[#E5E7EB] bg-[#F5F6F6] opacity-60 cursor-not-allowed"
          : checked
          ? "border-[#965EA7] bg-[#965EA7]/[0.06]"
          : "border-[#E5E7EB] hover:border-[#965EA7]/40"
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-[#373A45]">
        <span className={checked ? "text-[#965EA7]" : "text-[#6B7280]"}>{icon}</span>
        {title}
      </div>
      <div className="text-xs text-[#6B7280] mt-1">{hint}</div>
    </button>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${active ? "border-[#965EA7] text-[#965EA7]" : "border-transparent text-[#6B7280] hover:text-[#373A45]"}`}>
      {children}
    </button>
  );
}

function DetailsView({ lead, duplicates = [] }) {
  const utm = lead.utm || {};
  return (
    <>
      {duplicates.length > 0 && (
        <div className="rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4" data-testid="drawer-duplicates">
          <div className="inline-flex items-center gap-2 text-[#92400E] font-semibold text-sm">
            <AlertTriangle size={14} /> Posibil duplicat
          </div>
          <p className="mt-1 text-xs text-[#92400E]">
            Acest email apare și pe alte {duplicates.length} lead-{duplicates.length === 1 ? "" : "uri"}. Verifică înainte de a contacta.
          </p>
          <ul className="mt-3 space-y-1.5">
            {duplicates.map((d) => (
              <li key={d.id} className="text-xs text-[#373A45] bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 flex items-center justify-between">
                <span>
                  <strong>{d.prenume} {d.nume}</strong> · {d.telefon || "—"} · {d.source}
                </span>
                <span className="text-[#6B7280]">
                  {d.status} · {new Date(d.created_at).toLocaleString("ro-RO")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Section title="Date contact" icon={<User size={14} />}>
        <Item icon={<Mail size={13} />} label="Email" value={lead.email} />
        <Item icon={<Phone size={13} />} label="Telefon" value={lead.telefon} />
      </Section>

      <Section title="Profil profesor" icon={<BookOpen size={14} />}>
        <Item
          label="Ciclu de învățământ"
          value={
            Array.isArray(lead.teaching_levels) && lead.teaching_levels.length > 0
              ? [
                  ...lead.teaching_levels.filter((l) => l !== "Altul"),
                  ...(lead.teaching_levels.includes("Altul") && lead.teaching_level_other
                    ? [`Altul: ${lead.teaching_level_other}`]
                    : []),
                ].join(", ")
              : "—"
          }
          highlight
        />
        <Item
          label="Specializare"
          value={
            lead.specialization === "Altă disciplină" && lead.specialization_other
              ? `Altă disciplină: ${lead.specialization_other}`
              : lead.specialization || lead.specializare || "—"
          }
        />
      </Section>

      <Section title="Unitate de învățământ" icon={<Building2 size={14} />}>
        <Item
          label="Unitate"
          value={
            <span className="inline-flex items-center gap-1.5">
              {lead.unitate || "—"}
              {lead.manual_school_entry && (
                <span
                  className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#FFF7E6] text-[#7A5A1B] border border-[#F5C76E]/40"
                  title="Profesorul a introdus manual numele unității (nu a fost găsită în director)."
                >
                  manual
                </span>
              )}
            </span>
          }
          highlight
        />
        <Item label="Localitate (UAT)" value={lead.uat_name || lead.city || "—"} />
        <Item label="Județ" value={lead.county || "—"} />
        {lead.school_code && <Item label="Cod SIIIR" value={lead.school_code} mono />}
        {lead.city_original && lead.city_original !== lead.uat_name && (
          <Item label="Localitate (formular)" value={lead.city_original} />
        )}
      </Section>

      <Section title="Atribuire & tracking" icon={<MousePointer size={14} />}>
        <Item label="Sursă" value={leadSourceLabel(lead.source || "homepage_form")} highlight />
        <Item label="Popup trigger" value={lead.popup_trigger_type || "—"} />
        <Item label="Popup CTA clicked" value={lead.popup_cta_clicked ? "Da" : "—"} />
        <Item label="UTM source" value={utm.utm_source || "—"} />
        <Item label="UTM medium" value={utm.utm_medium || "—"} />
        <Item label="UTM campaign" value={utm.utm_campaign || "—"} />
        <Item label="UTM content" value={utm.utm_content || "—"} />
        <Item label="UTM term" value={utm.utm_term || "—"} />
        <Item label="Referrer" value={lead.referrer || "—"} />
        <Item label="Landing page" value={lead.landing_page || "—"} />
        <Item label="Creat la" value={new Date(lead.created_at).toLocaleString("ro-RO")} />
      </Section>

      <Section title="GDPR" icon={<ShieldCheck size={14} />}>
        <Item label="Privacy" value={lead.consent_privacy ? "✓ Acceptat" : "✗"} />
        <Item label="Marketing" value={lead.consent_marketing ? "✓ Acceptat" : "✗"} />
        <Item label="IP" value={lead.ip || "—"} />
        <Item label="User-agent" value={lead.user_agent || "—"} mono />
      </Section>

      <Section title="CRM" icon={<Tag size={14} />}>
        <Item label="Status" value={lead.status || "nou"} highlight />
        <Item label="Status update" value={lead.status_updated_at ? `${new Date(lead.status_updated_at).toLocaleString("ro-RO")} de ${lead.status_updated_by}` : "—"} />
        <Item label="Atribuit" value={lead.assigned_to || "—"} />
        <Item label="Note" value={String(lead.notes_count || 0)} />
      </Section>
    </>
  );
}

function TimelineView({ events }) {
  if (!events.length) return <p className="text-sm text-[#6B7280]">Niciun eveniment înregistrat.</p>;
  return (
    <ol className="relative border-l-2 border-[#E5E7EB] ml-3 space-y-4 pl-5" data-testid="drawer-timeline">
      {events.map((e) => (
        <li key={e.id} className="relative">
          <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full bg-[#965EA7] border-2 border-white" />
          <div className="text-xs text-[#6B7280]">{e.ts ? new Date(e.ts).toLocaleString("ro-RO") : "—"}</div>
          <div className="text-sm font-semibold text-[#373A45]">{EVENT_LABELS[e.type] || e.type}</div>
          {e.actor && <div className="text-xs text-[#6B7280]">de {e.actor}</div>}
          {e.details && Object.keys(e.details).length > 0 && (
            <div className="mt-1 text-xs text-[#373A45] bg-white border border-[#E5E7EB] rounded-lg px-2 py-1.5 font-mono">
              {Object.entries(e.details).filter(([, v]) => v !== null && v !== "").map(([k, v]) => (
                <div key={k}><strong>{k}:</strong> {String(v)}</div>
              ))}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

function NotesView({ notes, canMutate, noteText, setNoteText, saving, onSubmit }) {
  return (
    <>
      {canMutate && (
        <form onSubmit={onSubmit} className="bg-white border border-[#E5E7EB] rounded-2xl p-3 mb-4">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            placeholder="Adaugă o notiță internă (vizibilă doar în admin)..."
            className="w-full bg-transparent resize-none outline-none text-sm text-[#373A45] placeholder:text-[#6B7280]"
            data-testid="drawer-note-input"
          />
          <div className="flex items-center justify-end mt-2">
            <button
              type="submit"
              disabled={saving || !noteText.trim()}
              className="inline-flex items-center gap-1 rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-40 text-white px-4 py-1.5 text-xs font-semibold"
              data-testid="drawer-note-submit"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Adaugă notiță
            </button>
          </div>
        </form>
      )}
      <div className="space-y-2">
        {notes.length === 0 && <p className="text-sm text-[#6B7280]">Nicio notiță încă.</p>}
        {notes.slice().reverse().map((n) => (
          <div key={n.id} className="bg-white border border-[#E5E7EB] rounded-2xl p-3" data-testid={`drawer-note-${n.id}`}>
            <p className="text-sm text-[#373A45] whitespace-pre-wrap">{n.text}</p>
            <div className="text-[11px] text-[#6B7280] mt-1.5">
              {new Date(n.created_at).toLocaleString("ro-RO")} · {n.created_by}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-2xl p-4">
      <h3 className="font-display text-sm font-bold text-[#373A45] inline-flex items-center gap-2 mb-3">
        {icon} {title}
      </h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {children}
      </dl>
    </section>
  );
}

/**
 * Follow-up & activitate — dedicated section so the date input has a clear
 * label and context (was previously a bare `dd/mm/yyyy` chip in the header).
 * Persistence path unchanged: PATCH /admin/leads/:id { follow_up_date }.
 */
/**
 * Calls: when the next one is due, record one, and what came of the others.
 * The next call is the lead's follow-up date — one date, not two.
 */
function CallsSection({ lead, canMutate, onFollowUp, onLogged }) {
  const [history, setHistory] = useState(null);
  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get(`/admin/leads/${lead.id}/calls`);
      setHistory(r.data.items || []);
    } catch {
      setHistory([]);
    }
  }, [lead.id]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const next = lead.follow_up_date;
  const overdue = next && next < todayRo();
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-2xl p-4 space-y-3" data-testid="drawer-calls-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-bold text-[#373A45] inline-flex items-center gap-2">
          <Phone size={14} /> Apeluri {lead.calls_count ? <span className="font-normal text-[#6B7280]">({lead.calls_count})</span> : null}
        </h3>
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          Următorul apel:
          <div className="w-[150px]">
            <DatePickerField size="sm" value={next || ""} onChange={(v) => canMutate && onFollowUp(v)}
              ariaLabel="Data următorului apel" testid="drawer-next-call" />
          </div>
          {next && <span className={overdue ? "font-semibold text-[#9b1f2c]" : ""}>{nextCallLabel(next)}</span>}
        </div>
      </div>

      {canMutate && (
        <LeadCallPanel lead={lead} onLogged={(u) => { onLogged(u); loadHistory(); }} />
      )}

      {history && history.length > 0 && (
        <ol className="space-y-2 border-t border-[#E5E7EB] pt-3" data-testid="drawer-call-history">
          {history.map((c) => {
            const o = OUTCOME_BY_ID[c.outcome] || { label: c.outcome, cls: "bg-[#EDEEF0] text-[#373A45]" };
            return (
              <li key={c.id} className="text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${o.cls}`}>{o.label}</span>
                  <span className="text-xs text-[#6B7280]">
                    {new Date(c.at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })} · {c.by}
                  </span>
                  {c.next_call_date && (
                    <span className="text-xs text-[#6B7280]">→ revine pe {new Date(`${c.next_call_date}T12:00:00`).toLocaleDateString("ro-RO")}</span>
                  )}
                </div>
                {c.note && <p className="mt-0.5 text-[13px] text-[#373A45]">{c.note}</p>}
              </li>
            );
          })}
        </ol>
      )}
      {history && history.length === 0 && (
        <p className="text-xs text-[#6B7280]">Niciun apel înregistrat încă.</p>
      )}
    </section>
  );
}

function Item({ icon, label, value, highlight = false, mono = false }) {
  return (
    <div className="flex items-start gap-2">
      {icon && <span className="text-[#965EA7] mt-0.5">{icon}</span>}
      <div className="flex-1 min-w-0">
        <dt className="text-[11px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</dt>
        <dd className={`text-sm ${highlight ? "font-semibold text-[#7A4986]" : "text-[#373A45]"} ${mono ? "font-mono text-xs break-all" : "break-words"}`}>{value}</dd>
      </div>
    </div>
  );
}
